/**
 * TanStack Query hooks for Nameplate Scan API
 */

import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { useSelectedOrganizationId } from "@/organization/api";
import {
  NameplateScan,
  NameplateScanCreateResponse,
  NameplateScanAssignPayload,
  ScanStatus,
} from "./types";

/**
 * Query key factory for nameplate scans
 */
export const nameplateScans = createQueryKeys("nameplateScans", {
  detail: (scanId: string) => ({
    queryKey: [scanId],
    queryFn: async () => {
      const res = await apiClient.get<NameplateScan>(
        `/nameplate-scans/${scanId}/`
      );
      return res.data;
    },
  }),
});

/**
 * Upload nameplate image mutation
 * POST /api/nameplate-scans/
 */
export const useUploadNameplateScan = () => {
  const queryClient = useQueryClient();
  const organizationId = useSelectedOrganizationId();

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("organization", organizationId.toString());

      const res = await apiClient.post<NameplateScanCreateResponse>(
        "/nameplate-scans/",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return res.data;
    },
    onSuccess: (data) => {
      // Optimistically set the scan in the cache with processing status
      queryClient.setQueryData(
        nameplateScans.detail(data.id).queryKey,
        {
          id: data.id,
          status: data.status,
          exif_status: "pending",
          identification_status: "pending",
          is_ready: false,
          image_url: "",
          original_filename: "",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          error_message: "",
          exif_result: null,
          identification_result: null,
        } as NameplateScan
      );
    },
  });
};

/**
 * Upload nameplate image mutation
 * POST /api/nameplate-scans/
 */
export const useUploadNameplateScanWithOrganizationId = (organizationId: number) => {
  const queryClient = useQueryClient();
  

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("organization", organizationId.toString());

      const res = await apiClient.post<NameplateScanCreateResponse>(
        "/nameplate-scans/",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return res.data;
    },
    onSuccess: (data) => {
      // Optimistically set the scan in the cache with processing status
      queryClient.setQueryData(
        nameplateScans.detail(data.id).queryKey,
        {
          id: data.id,
          status: data.status,
          exif_status: "pending",
          identification_status: "pending",
          is_ready: false,
          image_url: "",
          original_filename: "",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          error_message: "",
          exif_result: null,
          identification_result: null,
        } as NameplateScan
      );
    },
  });
};
/**
 * Query hook for fetching a nameplate scan with auto-polling
 * GET /api/nameplate-scans/{id}/
 *
 * Auto-polling behavior:
 * - Polls every 1000ms while status is 'processing'
 * - Stops polling when status is 'completed' or 'failed'
 * - Completed scans have longer staleTime (5 minutes)
 */
export const useNameplateScan = (
  scanId: string | null | undefined,
  options?: {
    enabled?: boolean;
  }
) => {
  return useQuery({
    ...nameplateScans.detail(scanId!),
    enabled: !!scanId && (options?.enabled ?? true),
    staleTime: (query) => {
      const data = query.state.data as NameplateScan | undefined;
      // Completed scans are stale after 5 minutes
      // Processing scans are always stale (will refetch on interval)
      return data?.status === ScanStatus.COMPLETED ? 5 * 60 * 1000 : 0;
    },
    refetchInterval: (query) => {
      const data = query.state.data as NameplateScan | undefined;
      // Poll every 1 second while processing
      // Stop polling when completed or failed
      if (!data || data.status !== ScanStatus.PROCESSING) {
        return false;
      }
      return 1000;
    },
    refetchOnWindowFocus: false,
  });
};

/**
 * Assign nameplate scan to installation mutation
 * POST /api/nameplate-scans/{id}/assign/
 */
export const useAssignNameplateScan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { scanId: string; installationId: number }) => {
      const payload: NameplateScanAssignPayload = {
        installation_id: data.installationId,
      };

      const res = await apiClient.post(
        `/nameplate-scans/${data.scanId}/assign/`,
        payload
      );

      return res.data;
    },
    onSuccess: (_data, variables) => {
      // Invalidate the scan query to refetch updated data
      queryClient.invalidateQueries({
        queryKey: nameplateScans.detail(variables.scanId).queryKey,
      });
    },
  });
};